import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { Broadcast, Event, EventActionType, EventLiveSession, ParticipatedShortEvent } from '@/types/Event.type'
import { EventParticipantResponse } from '@/types/response/EventParticipantResponse.type'
import { EventActionsResponse } from '@/types/response/EventActionsResponse.type'
import { Card } from '@/types/Card.type'
import { PostEventActionResponse } from '@/types/response/PostEventActionResponse.type'
import { SuggestGuestToEventForm } from '@/types/form/SuggestGuestToEventForm'
import {
    ApplyPaymentForm,
    ApplyProfileForm,
    ApplyValidationForm,
    Bundle,
    CommunityApplyPaymentForm,
    CommunityApplyProfileForm,
    CommunityApplyValidationForm,
    Coupon,
    Flow,
} from '@/types/form/Apply'
import { UpcomingEventReponse } from '@/types/response/UpcomingEventReponse'
import { PaginatedEventListResponse } from '@/types/response/PaginatedEventListResponse.type'
import { GetMemberPaymentResponse } from '@/types/response/GetMemberPaymentResponse.type'
import { GetPublicParticipantPaymentResponse } from '@/types/response/GetPublicParticipantPaymentResponse.type'
import { MyEventsResponse } from '@/types/response/MyEventsResponse.type'
import { Guest } from '@/types/Guest.type'
import { GetStripeSetupIntentResponse } from '@/types/response/GetStripeSetupIntentResponse.type'
import { MemberSuggestion } from '@/types/MemberSuggestion.type'
import { Member, MemberActivityStats, MemberForAdmin, MemberSingle } from '@/types/Member.type'
import { Paginated } from '@/types/Paginated.type'
import {
    Club,
    ClubBoard,
    ClubDonation,
    ClubEvent,
    ClubMember,
    ClubMemberMeta,
    ClubNews,
    ClubProject,
    ClubSingle,
} from '@/types/Club.type'
import { BackstageInfoResponse } from '@/types/response/BackstageInfoResponse.type'
import { ChargebeeSubscription, GetMembershipDetailsResponse } from '@/types/response/GetMembershipDetailsResponse'
import { PaymentMethodResponse } from '@/types/response/PaymentMethodResponse'
import { GetRenewalDetailsResponse } from '@/types/response/GetRenewalDetailsResponse'
import { AutoRenewalForm, RenewalForm } from '@/types/form/RenewalForm'
import { FavoritePaymentMethodForm, PaymentMethodForm } from '@/types/form/PaymentMethodForm'
import { GetMembershipUnsubscribeDetailsResponse } from '@/types/response/GetMembershipUnsubscribeDetailsResponse'
import { Room } from '@/types/Room.type'
import { Login, Register } from '@/types/Login.type'
import { Lead } from '@/types/apply/Lead.type'
import { UserTokenDecoded } from '@/types/UserTokenDecoded.type'
import { SearchType } from '@/types/search/SearchType.type'
import { SearchHistory } from '@/types/search/SearchHistory.type'
import { GoalContent } from '@/types/Goal.type'
import { MemberActivityTab } from '@/utils/MemberActivityTab'
import {
    Benefit,
    BenefitCategory,
    BenefitCity,
    BenefitCompany,
    BenefitParams,
    FullBenefit,
} from '@/types/Benefits.type'
import router from '@/router'
import { Workspace } from '@/types/Workspaces.type'
import { Meta, MetaPager, ResponseV3 } from '@/types/API.type'
import { Header } from '@/types/Header.type'
import { Project } from '@/types/Project.type'
import { Hub, HubEvent, HubMember, HubMemberMeta, HubSum } from '@/types/Hub.type'
import { Trip, TripAddForm, TripEventsAround, TripSingle } from '@/types/Trip.type'
import { Invitation, InvitationItem, Sponsorship } from '@/types/Sponsorship.type'
import store from '@/store/store'
import { PartnerDomains, PartnerEvent, PartnerItem, PartnerMember, PartnerSingle } from '@/types/Partner.type'
import { MyPendingSuggestionResponse } from '@/types/response/MyPendingSuggestionResponse.type'
import { PendingRelationshipSuggestionForMemberResponse } from '@/types/response/PendingRelationshipSuggestionForMemberResponse.type'
import { ClaimableNFT } from '@/types/nft/ClaimableNFT.type'
import { Stake } from '@/types/Stake.type'
import { Feedback } from '@/types/Feedback.type'
import {
    AgreementNeedsMemberHelping,
    AgreementSuggestionsMembers,
    AgreementSuggestionsMutualInterest,
    AgreementSuggestionsPartners,
    AgreementSuggestionsPeers,
    AgreementType,
    NeedToSlack,
} from '@/types/Agreement.type'
import { EditExperienceForm } from '@/types/form/member/EditExperienceForm'
import { EditExperienceResponse } from '@/types/response/EditExperienceResponse'
import SharedStorage from '@/utils/SharedStorage'
import { jwtTokenName, refreshJwtTokenName } from '@/utils/auth'
import { InvestmentInquiryFormDTO } from '@/types/form/InvestmentInquiryForm'
import { GetTeamResponse } from '@/types/response/GetTeamResponse.type'
import { GetHomeOverviewResponse } from '@/types/response/GetOverviewHomeResponse'
import { GetSuggestedEventsResponse } from '@/types/response/GetSuggestedEventsResponse'
import { GetHomeBannerResponse } from '@/types/response/GetHomeBannerResponse'
import { GetDiscoverClubsResponse } from '@/types/response/GetDiscoverClubsResponse'
import { GetMemberInTripResponse } from '@/types/response/GetMemberInTripResponse'
import { GetNextEventsInHubResponse } from '@/types/response/GetNextEventsInHubResponse'
import { GetLastBatchResponse } from '@/types/response/GetLastBatchResponse'
import { RegisterData } from '@/types/RegisterData.type'
import { RegisterTokenInfo } from '@/types/RegisterTokenInfo.type'
import { GetPartnersForm } from '@/types/form/GetPartnersForm.type'

// @ts-ignore
const currentJwt = window.currentJwt

const API_URL = process.env.VUE_APP_API_BASE_URL as string
const GLADOS_PASSWORD = process.env.VUE_APP_GLADOS_PASSWORD as string
const PRIVATE_URL = API_URL.replace(/api/gi, 'private')

function getHeaders() {
    const headers: Record<string, unknown> = {
        Authorization: currentJwt ? `Bearer ${currentJwt}` : '',
        'X-Accept-Version': '2.1',
        'X-Origin': 'web',
        'x-ff-platform': 'web',
        'x-ff-context': 'club',
        'Accept-Language': store.getters['user/getLocale'] || undefined,
        'X-Glados': GLADOS_PASSWORD || undefined,
    }

    if (!headers.Authorization) {
        delete headers.Authorization
    }

    if (!headers['X-Glados']) {
        delete headers['X-Glados']
    }

    return headers
}

const http = axios.create({
    headers: getHeaders(),
})

async function forceRefreshUser(response: any) {
    const newJwtToken = response && response.headers && response.headers['ff-refresh-user']
    if (newJwtToken) {
        const config: AxiosRequestConfig = {
            headers: {
                Authorization: `Bearer ${newJwtToken}`,
            },
        }

        try {
            console.warn('[API] Force login ...')
            await store.dispatch('user/setToken', { token: newJwtToken })
            await http.post(`${PRIVATE_URL}/auth/force-login`, {}, config)
        } catch (e) {
            store.dispatch('user/logout', '/login')
        }
    }
}

class API {
    static _setBearer(token?: string | null) {
        if (token) {
            http.defaults.headers.Authorization = `Bearer ${token}`
        } else {
            delete http.defaults.headers.Authorization
        }
    }

    static async get(url: string) {
        const response = await http.get(url)
        return response.data
    }

    static getResponse(url: string): Promise<AxiosResponse<any>> {
        return http.get(url, { withCredentials: url.includes(PRIVATE_URL) })
    }

    /**
     * Get token of the current logged in user.
     */
    static async getAuthToken(): Promise<{ token: string }> {
        const response = await http.get(`${PRIVATE_URL}/auth/token`)
        return response.data
    }

    static async exitSwitchTo(): Promise<{ token: string }> {
        const response = await http.get(`${PRIVATE_URL}/`, {
            params: {
                _switch_user: '_exit',
            },
        })
        return response.data
    }

    static async getMyPendingSuggestions(): Promise<ResponseV3<MyPendingSuggestionResponse, Meta>> {
        const response = await http.get(`${API_URL}/v3/suggestions/pending`)
        return response.data
    }

    static async sendWalletAddress(addr: string) {
        const response = await http.post(`${API_URL}/v3/nft/wallet`, { addr })
        return response.data
    }

    static async getClaimableNFTList(): Promise<ResponseV3<ClaimableNFT[], { walletAddr: string }>> {
        const response = await http.get(`${API_URL}/v3/nft`)
        return response.data
    }

    static async claimNFT(nftId: string) {
        const response = await http.post(`${API_URL}/v3/nft/${nftId}/claim`)
        return response.data
    }

    static async postRelationshipSuggestionAnswer(
        relationhshipSuggestionId: string | number,
        isInterested: boolean,
        comment?: string
    ): Promise<ResponseV3<{ id: string }>> {
        const response = await http.post(`${API_URL}/v3/suggestions/${relationhshipSuggestionId}/answer`, {
            interested: isInterested,
            comment,
        })
        return response.data
    }

    /**
     * @deprecated Deprecated after moving to Chargebee
     */
    static async getStripeSetupIntent(): Promise<GetStripeSetupIntentResponse> {
        const response = await http.get(`${API_URL}/stripe/setup-intent`)
        return response.data
    }

    static async getMemberPaymentInfos(eventId: any, isStreaming?: boolean): Promise<GetMemberPaymentResponse> {
        const response = await http.get(`${API_URL}/event-v2/${eventId}/payment`, {
            params: {
                streaming: !!isStreaming,
            },
        })
        return response.data
    }

    static async getGuestInvited(eventId: string | number): Promise<Array<Guest>> {
        const response = await http.get(`${API_URL}/event-v2/${eventId}/guests`)
        return response.data
    }

    static async getSearchOptions(): Promise<{
        business_centers: Array<string>
        dates: Array<string>
    }> {
        const response = await http.get(`${API_URL}/public/event-v2/search/filters`)
        return response.data
    }

    static async getPublicPaymentInfos(
        eventId: any,
        publicParticipantId: string,
        isStreaming?: string
    ): Promise<GetPublicParticipantPaymentResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/${eventId}/payment/${publicParticipantId}`, {
            params: {
                streaming: !!isStreaming,
            },
        })
        return response.data
    }

    static async postUseCoupon(
        eventId: any,
        code: string,
        isStreaming?: boolean,
        publicParticipantId?: string | null
    ): Promise<Coupon> {
        const response = await http.post(`${API_URL}/public/event-v2/${eventId}/coupon/${code}`, {
            streaming: !!isStreaming,
            publicParticipantId,
        })
        return response.data
    }

    static async getSuggestedEventList(params?: { webinar: boolean }): Promise<Array<Event>> {
        const response = await http.get(`${API_URL}/event-v2/suggested`, {
            params,
        })

        return response.data
    }

    static async getSuggestedReplayList(eventId: number): Promise<Array<Event>> {
        const response = await http.get(`${API_URL}/event-v2/suggested/${eventId}/replay`)

        return response.data
    }

    static async getWelcomeBreakfastEvents(): Promise<{
        nextInBC: Event
        nexts: Array<Event>
    }> {
        const response = await http.get(`${API_URL}/event-v2/welcome-breakfasts`)

        return response.data
    }

    static async getMyEvents(): Promise<ResponseV3<MyEventsResponse>> {
        const response = await http.get(`${API_URL}/v3/events/me`)

        return response.data.data
    }

    static async getMyCards(): Promise<Array<Card>> {
        const response = await http.get(`${API_URL}/membership/cards`)

        return response.data
    }

    static async getUpcomingEvents(params: {
        categoryIds?: string
        notCategoryIds?: Array<number>
        page?: number
    }): Promise<UpcomingEventReponse> {
        const response = await http.get(`${API_URL}/public/event-v2/incoming`, { params })

        return response.data
    }

    static async postEventAction(
        eventId: number | string,
        actionKey: EventActionType,
        actionParams?: Record<string, any>
    ): Promise<AxiosResponse<PostEventActionResponse>> {
        return await http.post(`${API_URL}/event-v2/${eventId}/action/${actionKey}`, { ...actionParams })
    }

    static async postPublicEventAction(
        eventId: number | string,
        actionKey: string,
        actionParams?: Record<string, any>,
        publicParticipantId?: string | null
    ): Promise<PostEventActionResponse> {
        const response = await http.post(
            `${API_URL}/public/event-v2/${eventId}/action/${actionKey}`,
            { ...actionParams, publicParticipantId },
            { params: { publicParticipantId } }
        )

        return response.data
    }

    static async postStreamEventAction(
        eventId: number,
        actionKey: string,
        actionParams?: Record<string, any>
    ): Promise<AxiosResponse<PostEventActionResponse>> {
        return await http.post(`${API_URL}/event-stream/${eventId}/action/${actionKey}`, { ...actionParams })
    }

    static async postPublicStreamEventAction(
        eventId: number,
        actionKey: string,
        actionParams?: Record<string, any>,
        publicParticipantId?: string | number
    ): Promise<PostEventActionResponse> {
        const response = await http.post(
            `${API_URL}/public/event-stream/${eventId}/action/${actionKey}`,
            { ...actionParams, publicParticipantId },
            { params: { publicParticipantId } }
        )

        return response.data
    }

    static async postEventStreamReplayRequest(eventId: number) {
        const response = await http.post(`${API_URL}/event/${eventId}/request-replay`)
        return response
    }

    static async getPastEvents(page = 1): Promise<PaginatedEventListResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/pasts`, { params: { page } })

        return response.data
    }

    static async postEventAskToSponsor(eventId: number | string): Promise<AxiosResponse> {
        return await http.post(`${API_URL}/v3/events/${eventId}/ask-to-sponsor`)
    }

    static async askRelationMember(participantIdFF: string, eventId: string | number): Promise<unknown> {
        const response = await http.post(`${API_URL}/v3/networking/confirm`, {
            memberIdFF: participantIdFF,
            eventId: eventId,
        })
        return response?.data
    }

    static async getConfiguration(): Promise<Record<string, any>> {
        const response = await http.get(`${API_URL}/configuration`)
        return response.data
    }

    static async searchPhysicalEvents(params: {
        businessCenters?: string
        startDate?: string
        endDate?: string
        search?: string
        page?: number
        size?: number
        notCategoryIds?: Array<number>
        hub?: 'inHub' | 'outHub'
        placeId?: string
    }): Promise<PaginatedEventListResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/search/upcoming/physical`, { params })

        return response.data
    }

    static async searchAllEvents(params: {
        businessCenters?: string
        startDate?: string
        endDate?: string
        search?: string
        page?: number
        size?: number
        notCategoryIds?: Array<number>
    }): Promise<PaginatedEventListResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/search/upcoming/all`, { params })

        return response.data
    }

    static async searchStreamingEvents(params: {
        businessCenters?: string
        startDate?: string
        endDate?: string
        search?: string
        page?: number
        size?: number
        webinar?: boolean
        onlyStreaming?: boolean
    }): Promise<PaginatedEventListResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/search/upcoming/remote`, { params })

        return response.data
    }

    static async searchPastEvents(params: {
        businessCenters?: string
        startDate?: string
        endDate?: string
        search?: string
        page?: number
        webinar?: boolean
    }): Promise<PaginatedEventListResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/search/past`, { params })

        return response.data
    }

    static async getEventDetails(eventId: number | string, leadId?: string | number): Promise<Event> {
        const response = await http.get(`${API_URL}/public/event-v2/${eventId}/details`, {
            params: {
                leadId,
            },
        })

        return response.data
    }
    static async getEventActions(
        eventId: number | string,
        publicParticipantId?: any,
        lang?: string
    ): Promise<EventActionsResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/${eventId}/actions`, {
            params: { publicParticipantId },
            headers: lang
                ? {
                      'Accept-Language': lang,
                  }
                : undefined,
        })

        return response.data
    }

    static async getEventParticipants(eventId: number | string, leadId?: string): Promise<EventParticipantResponse> {
        const response = await http.get(`${API_URL}/public/event-v2/${eventId}/participants`, {
            params: {
                leadId,
            },
        })

        return response.data
    }

    static async getEventReplays(eventId: number | string): Promise<Event> {
        const response = await http.get(`${API_URL}/event-v2/${eventId}/replays`)

        return response.data
    }

    static async getEventStreamReplayRequest(eventId: number) {
        const response = await http.get(`${API_URL}/event/${eventId}/request-replay`)
        return response
    }

    static async getMe() {
        const response = await http.get(`${API_URL}/member/me`)
        return response.data
    }

    static async getMember(memberId: string): Promise<Member> {
        const { data }: { data: Member } = await http.get(`${API_URL}/members/${memberId}`)
        return data
    }

    static async getMemberSingle(memberId: string): Promise<MemberSingle> {
        const response = await http.get(`${API_URL}/v3/members/${memberId}`)
        return response.data.data
    }

    static async getMemberEvents(
        memberId: string,
        type: 'next' | 'recommended' | 'past',
        params?: { page: number; size: number }
    ): Promise<ResponseV3<ParticipatedShortEvent[], MetaPager>> {
        const response = await http.get(`${API_URL}/v3/members/${memberId}/events/${type}`, {
            params,
        })
        return response.data
    }

    static async getMemberForAdmin(memberId: string): Promise<MemberForAdmin> {
        const response = await http.get(`${API_URL}/v3/admin/members/${memberId}`)
        return response && response.data && response.data.data
    }

    static async getPendingRelationshipSuggestionForMember(
        memberId: string | number
    ): Promise<ResponseV3<PendingRelationshipSuggestionForMemberResponse>> {
        const response = await http.get(`${API_URL}/v3/suggestions/pending/${memberId}`)
        return response.data
    }

    static async patchSelfMember(memberData: any) {
        const response = await http.patch(`${API_URL}/member/me/details`, memberData)
        return response.data
    }

    static async patchMember(memberId: string, properties: any): Promise<{ member: Member }> {
        const response = await http.patch(`${API_URL}/members/${memberId}/details`, properties)
        return response.data
    }

    static async patchMe(properties: any): Promise<{ jwt?: { token: string } }> {
        const response = await http.patch(`${API_URL}/v3/members/me`, properties)
        return response.data
    }

    static async patchMeAvatar(file: string): Promise<void> {
        const response = await http.post(`${API_URL}/v3/members/me/picture`, { file })
        return response.data
    }

    static async favoriteMember(memberId: string) {
        const response = await http.post(`${API_URL}/members/${memberId}/likes`)
        return response.data
    }

    static async unfavoriteMember(memberId: string) {
        const response = await http.delete(`${API_URL}/members/${memberId}/likes`)
        return response.data
    }

    static async editLabels(memberId: string, labels: any[]): Promise<Member> {
        const labelIds = labels.map(label => label.id)

        const response = await http.put(`${API_URL}/members/${memberId}/labels`, { labels: labelIds })
        return response.data
    }

    static async editPresentation(presentation: any) {
        const response = await http.put(`${API_URL}/member/me/presentation`, { presentation })

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async contactFrenchFounders(subject: string, message: string): Promise<void> {
        const response = await http.post(`${API_URL}/v3/contact`, {
            subject,
            message,
        })

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async getGoalList(): Promise<GoalContent[]> {
        const response = await http.get(`${API_URL}/member/goal/availables`)
        return response.data
    }

    static async editGoals(memberId: string, goals: any[]): Promise<Member> {
        const goalIds = goals.map(goal => goal.id)

        const response = await http.post(`${API_URL}/members/${memberId}/goals`, {
            goals: goalIds,
        })

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async getExpertises() {
        const response = await http.get(`${API_URL}/member/expertises/availables`)
        return response.data
    }

    static async getInterestList() {
        const response = await http.get(`${API_URL}/member/interest/availables`)
        return response.data
    }

    static async editExpertises(expertises: any): Promise<Member> {
        const expertiseIds = expertises.map((e: any) => e.id)

        const response = await http.put(`${API_URL}/member/me/expertises`, { expertises: expertiseIds })
        return response.data
    }

    static async getNationalitiesAvailable() {
        const response = await http.get(`${API_URL}/member/available-nationalities`)
        return response.data
    }

    static async editNationalities(
        memberId: string,
        { nationalities, spokenLanguages, showNationalityOnProfile }: any
    ): Promise<Member> {
        const response = await http.put(`${API_URL}/members/${memberId}/nationalities`, {
            nationalities,
            spokenLanguages,
            showNationalityOnProfile,
        })

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async getAdditionnalAdminInformations(memberId: string) {
        const response = await http.get(`${API_URL}/members/${memberId}/admin/additional/informations`)
        return response.data
    }

    static async editExperience(
        id: number,
        experience: EditExperienceForm
    ): Promise<ResponseV3<EditExperienceResponse>> {
        const response = await http.patch(`${API_URL}/v3/members/experiences/${id}`, experience)

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async createExperience(experience: EditExperienceForm): Promise<ResponseV3<EditExperienceResponse>> {
        const response = await http.post(`${API_URL}/v3/members/experiences`, experience)

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async deleteExperience(experienceId: number) {
        const response = await http.delete(`${API_URL}/member/me/${experienceId}/experience`)

        if (response.status > 300) {
            throw response
        }

        return response.data
    }

    static async getHubs(all = false): Promise<ResponseV3<HubSum[]>> {
        const response = await http.get(`${API_URL}/v3/hubs?all=${all}`)
        return response.data
    }

    static async getBusinessChallenges() {
        const response = await http.get('/member/business-category/availables')
        return response.data
    }

    static async sendRelationshipRequest(memberId: string, message: string) {
        const params = {
            memberId: memberId,
            comment: message,
        }

        const response = await http.post(`${API_URL}/v3/relationship-ask`, params)
        return response.data
    }

    static async sendSuggestions(memberIdff: string, { members, message, timezone, scheduledAt }: any) {
        const form = new FormData()

        members.forEach((memberId: string, i: number) => form.append(`members[${i}]`, memberId))

        form.append('message', message)
        form.append('timezone', timezone)
        form.append('scheduledAt', scheduledAt)

        const response = await http.post(`/member/${memberIdff}/recommendation/send`, form)
        return response.data
    }

    static async getNotifications() {
        const response = await http.get(`${API_URL}/notifications`)
        return response.data
    }

    static async postApplyProfile(form: ApplyProfileForm) {
        const response = await http.post(`${API_URL}/apply/profile`, form)
        return response.data
    }

    static async getApplyClubs(): Promise<Club[]> {
        const response = await http.get(`${API_URL}/apply/available-clubs`)
        return response.data
    }

    static async postApplyCode({ leadId, planId, couponCode }: any): Promise<Coupon> {
        const response = await http.post(`${API_URL}/apply/payment/coupons`, { leadId, planId, couponCode })
        return response.data
    }

    static async deleteApplyCoupon(leadId: string): Promise<any> {
        const response = await http.delete(`${API_URL}/apply/payment/coupons/${leadId}`)
        return response.data
    }

    static async getApplyAvailableCountries(lang: string) {
        const headers: any = {}

        if (lang) {
            headers['Accept-Language'] = lang
        }

        const response = await http.get(`${API_URL}/apply/available-countries`, { headers })
        return response.data
    }

    static async getApplyPlan(leadId: string, context?: object): Promise<Bundle> {
        const response = await http.post(`${API_URL}/apply/available-bundles`, { leadId, context })
        return response.data
    }

    static async postApplyMembership(leadId: string, clubs: Club[], gwycDiscount = 0): Promise<Lead> {
        const clubIds = clubs.map(club => club.id)

        const response = await http.post(`${API_URL}/apply/membership`, {
            leadId,
            clubIds,
            gwycDiscount,
        })
        return response.data
    }

    static async postApplyPayment(form: ApplyPaymentForm) {
        const response = await http.post(`${API_URL}/apply/payment`, form)
        return response.data
    }

    static async postApplyValidation(form: ApplyValidationForm) {
        const response = await http.post(`${API_URL}/apply/validation`, form)
        return response.data
    }

    static async postSearchHeaderAutocomplete(search: string, cancelToken: any = null) {
        try {
            const response = await http.post(
                `${API_URL}/v2/search/header/autocomplete`,
                { search: search },
                { cancelToken }
            )
            return response.data
        } catch (e) {
            if (axios.isCancel(e)) {
                console.log('Request canceled', e.message)
            }
            throw e
        }
    }

    static async suggestGuestToEvent(eventId: number, form: SuggestGuestToEventForm) {
        try {
            const response = await http.post(`${API_URL}/events/${eventId}/invitations`, form)
            return response.data
        } catch (e) {
            if (axios.isCancel(e)) {
                console.log('Request canceled', e.message)
            }
            throw e
        }
    }

    static async editTranslation(locale: string, domain: string, key: string, value: string) {
        try {
            const response = await http.post(
                // @ts-ignore
                window.Routing.generate('translation_edit_in_place_update', {
                    configName: 'app',
                    locale: locale,
                }),
                {
                    [`${domain}|${key}`]: value,
                }
            )
            return response.data
        } catch (e) {
            if (axios.isCancel(e)) {
                console.log('Request canceled', e.message)
            }
            throw e
        }
    }

    static async postEventDirectPayment(eventId: number, form: any) {
        try {
            const response = await http.post(`/event-v2/${form.eventId}/direct-payment`, form)
            return response.data
        } catch (e) {
            if (axios.isCancel(e)) {
                console.log('Request canceled', e.message)
            }
            throw e
        }
    }

    static async getMemberPendingSuggestionList(filters?: any): Promise<Paginated<MemberSuggestion>> {
        const suggestionList = await http.post(`${API_URL}/v2/search/my-suggestions`, filters)
        return suggestionList.data
    }

    static async postSuggestionInterest(suggestionId: number, isInterested: boolean, comment?: string): Promise<any> {
        const suggestionInterest = await http.post(
            `${API_URL}/relationships/${suggestionId}/marks/${Number(isInterested)}/suggestions`,
            { comment }
        )
        return suggestionInterest.data
    }

    static async searchForMembers(name: string /*, size = 100, page = 1*/) {
        const response = await http.post(`${API_URL}/v2/search/header/search-all/member`, { searchText: name })
        return response.data
    }

    static async getMemberActivity(memberId: string): Promise<ResponseV3<MemberActivityStats>> {
        const response = await http.get(`${API_URL}/v3/admin/members/${memberId}/activity/stats`)

        return response.data
    }

    static async getMemberActivityTab(memberId: string, tab: MemberActivityTab) {
        const response = await http.get(`${API_URL}/member/activity/${tab}/${memberId}`)

        return response.data
    }

    static async searchMembers(
        filters?: any,
        page = 1,
        sort: 'scoring' | 'newest' | 'lastName' = 'scoring'
    ): Promise<Paginated<Member>> {
        const members = await http.post(`${API_URL}/v2/search/members?page=${page}&sort=${sort}`, filters)
        return members.data
    }

    static async searchMembersFilters(filters?: any): Promise<any> {
        const members = await http.post(`${API_URL}/v2/search/filters/members`, filters)
        return members.data
    }

    static async getMyContactMemberList(
        filters?: any,
        page = 1,
        sort: 'scoring' | 'newest' | 'lastName' = 'scoring'
    ): Promise<Paginated<Member>> {
        const members = await http.post(`${API_URL}/v2/search/my-contacts?page=${page}&sort=${sort}`, filters)
        return members.data
    }

    static async getMyFavoriteMemberList(
        filters?: any,
        sort: 'scoring' | 'newest' | 'lastName' = 'scoring'
    ): Promise<Paginated<Member>> {
        const members = await http.post(`${API_URL}/v2/search/my-favorites?sort=${sort}`, filters)
        return members.data
    }

    static async getMembersWithSameEvent(): Promise<Array<Member>> {
        const members = await http.get(`${API_URL}/v2/search/members-with-same-events`)
        return members.data
    }

    static async getSearchAutocomplete(search: string): Promise<any> {
        const members = await http.get(`${API_URL}/v2/search/autocomplete/filters/members?search=${search}`)
        return members.data
    }

    static async requestRelationship(member: Member, comment: string): Promise<{ success: boolean }> {
        const response = await http.post(`${API_URL}/relationships/${member.id}/asks`, { comment })
        return response.data
    }

    static async sendMemberListEmails(filters: any, subject: string, body: string): Promise<any> {
        const response = await http.post(`${API_URL}/v2/search/send-list-email/members`, { filters, subject, body })
        return response.data
    }

    // static async getLiveEventAuthData(eventId: number | string): Promise<{ realtimeToken: string }> {
    //     const response = await http.get(`${API_URL}/event-streaming/${eventId}/auth`)
    //     return response.data
    // }

    static async getLiveEventSchedule(eventId: number | string): Promise<Array<EventLiveSession>> {
        const response = await http.get(`${API_URL}/event-streaming/${eventId}/schedule`)
        return response.data
    }

    static async loginLiveEvent(
        eventId: number | string,
        email?: string,
        code?: string
    ): Promise<{ jwtToken: string }> {
        const response = await http.post(`${PRIVATE_URL}/event-streaming/${eventId}/auth`, { email, code })
        return response.data
    }

    static async resendCodeEvent(eventId: number | string, email?: string): Promise<any> {
        const response = await http.post(`${API_URL}/event-streaming/${eventId}/auth/send-code`, { email })
        return response.data
    }

    static async postLiveInterest(
        localParticipant: UserTokenDecoded,
        remoteParticipant: UserTokenDecoded,
        eventId: string | number
    ): Promise<any> {
        const form: any = {
            eventId,
        }

        if (localParticipant.type === 'member') {
            form.memberId = localParticipant.id
        } else {
            form.leadId = localParticipant.id
        }

        if (remoteParticipant.type === 'member') {
            form.interestedInMemberId = remoteParticipant.id
        } else {
            form.interestedInLeadId = remoteParticipant.id
        }

        const response = await http.post(`${API_URL}/public/networking/interest`, form)
        return response.data
    }

    static async sendNetworkingConnection(
        localParticipant: UserTokenDecoded,
        remoteParticipant: UserTokenDecoded,
        eventId: string | number,
        twillioId: string
    ): Promise<any> {
        const form: any = {
            eventId,
        }

        if (localParticipant.type === 'member') {
            form.firstMemberId = localParticipant.id
        } else {
            form.firstLeadId = localParticipant.id
        }

        if (remoteParticipant.type === 'member') {
            form.secondMemberId = remoteParticipant.id
        } else {
            form.secondLeadId = remoteParticipant.id
        }

        form.twillioId = twillioId

        const response = await http.post(`${API_URL}/public/networking/contact`, form)
        return response.data
    }

    static async requestBackstageInfo(
        eventId: number | string,
        leadId?: number | string
    ): Promise<BackstageInfoResponse> {
        const response = await http.post(`${API_URL}/backstage/${eventId}`, {
            leadId,
        })
        // Can be a redirect to /login, throw error in this case
        if (response.headers['content-type'] !== 'application/json') {
            throw Error('Not logged')
        }

        return response.data
    }

    static async startEventBroadcasting(
        eventId: number | string,
        sessionID: string,
        record = false,
        rtmpPlatform: string | 'AmazonIVS',
        rtmpUrl: string,
        rtmpStreamKey: string,
        playbackUrl: string
    ): Promise<Broadcast> {
        const response = await http.post(`${API_URL}/backstage/${eventId}/streaming`, {
            action: 'start',
            sessionId: sessionID,
            record,
            rtmpPlatform,
            rtmpUrl,
            rtmpStreamKey,
            playbackUrl,
        })
        return response.data
    }

    static async stopEventBroadcasting(eventId: number | string, sessionID: string): Promise<any> {
        const response = await http.post(`${API_URL}/backstage/${eventId}/streaming`, {
            action: 'stop',
            sessionId: sessionID,
        })
        return response.data
    }

    static async isBroadcastingLiveReady(broadcastId: string): Promise<Broadcast> {
        const response = await http.get(`${API_URL}/backstage/streaming/${broadcastId}/is-ready`)
        return response.data
    }

    static async updateBroadcastLayout(eventId: number | string, hiddenStreams: Array<string>): Promise<any> {
        const response = await http.post(`${API_URL}/backstage/${eventId}/streaming/layout`, {
            hiddenStreams: hiddenStreams,
        })
        console.log('layout updated', response.data)
        return response.data
    }

    static async forceBroadcastLayout(eventId: number | string, layout: string): Promise<any> {
        const response = await http.put(`${API_URL}/backstage/${eventId}/streaming/force-layout`, { layout: layout })
        return response.data
    }

    static async getMembershipDetails(): Promise<GetMembershipDetailsResponse> {
        const response = await http.get(`${API_URL}/membership-details`)
        return response.data
    }

    static async getSubscriptions(): Promise<Array<ChargebeeSubscription>> {
        const response = await http.get(`${API_URL}/v3/subscriptions`)
        return response.data.data
    }

    static async getSubscriptionData(
        subscriptionId: string,
        params: { planItemPriceId?: string; addonItemPriceId?: string; context?: string }
    ): Promise<ResponseV3<Record<string, unknown>>> {
        const response = await http.put(`${API_URL}/v3/subscriptions/${subscriptionId}`, params)
        return response.data
    }

    static async getPayloadUpgradeMembership(): Promise<ResponseV3<Record<string, unknown>>> {
        const response = await http.get(`${API_URL}/v3/subscriptions/upgrade`)
        return response.data
    }

    static async putMembershipBillingAddress(form: Record<string, any>): Promise<any> {
        const response = await http.put(`${API_URL}/billingAddress`, form)

        return response.data
    }

    static async postPaymentMethod(form: PaymentMethodForm): Promise<PaymentMethodResponse> {
        if (typeof form.paymentMetas !== 'string') {
            form.paymentMetas = JSON.stringify(form.paymentMetas)
        }

        const response = await http.post(`${API_URL}/payment-method`, form)

        return response.data
    }

    static async putPaymentMethod(paymentMethodId: string, form: PaymentMethodForm): Promise<PaymentMethodResponse> {
        if (typeof form.paymentMetas !== 'string') {
            form.paymentMetas = JSON.stringify(form.paymentMetas)
        }

        const response = await http.put(`${API_URL}/payment-method/${paymentMethodId}`, form)

        return response.data
    }

    static async deletePaymentMethod(paymentMethodId: string): Promise<any> {
        const response = await http.delete(`${API_URL}/payment-method/${paymentMethodId}`)

        return response.data
    }

    static async postUpdateFavoritePaymentMethod(form: FavoritePaymentMethodForm): Promise<PaymentMethodResponse> {
        if (typeof form.paymentMetas !== 'string') {
            form.paymentMetas = JSON.stringify(form.paymentMetas)
        }

        const response = await http.post(`${API_URL}/favorite-payment-method`, form)

        return response.data
    }

    static async getRenewalDetails(): Promise<GetRenewalDetailsResponse> {
        const response = await http.get(`${API_URL}/membership/renewal`)
        return response.data
    }

    static async postMembershipRenewal(form: RenewalForm): Promise<PaymentMethodResponse | null> {
        const response = await http.post(`${API_URL}/membership/renewal`, form)

        return response.data
    }

    static async postMembershipChangePlan(form: RenewalForm, requestId: string): Promise<PaymentMethodResponse | null> {
        const response = await http.post(`${API_URL}/membership/changing-plan/${requestId}/pay`, form)

        return response.data
    }

    static async postPayNextMembershipRenewal(form: RenewalForm): Promise<PaymentMethodResponse | null> {
        const response = await http.post(`${API_URL}/membership/manual-renewal/pay`, form)

        return response.data
    }

    static async getRenewalAddonDetails(addonSubscriptionId: string): Promise<GetRenewalDetailsResponse> {
        const response = await http.get(`${API_URL}/membership/addon-renewal/${addonSubscriptionId}`)
        return response.data
    }

    static async postAddonRenewal(
        addonSubscriptionId: string,
        form: RenewalForm
    ): Promise<PaymentMethodResponse | null> {
        const response = await http.post(`${API_URL}/membership/addon-renewal/${addonSubscriptionId}`, form)

        return response.data
    }

    static async postPayNextAddonRenewal(
        addonSubscriptionId: string,
        form: RenewalForm
    ): Promise<PaymentMethodResponse | null> {
        const response = await http.post(`${API_URL}/membership/manual-addon-renewal/${addonSubscriptionId}/pay`, form)

        return response.data
    }

    static async postSwitchAutoRenewal(form: AutoRenewalForm): Promise<any> {
        const response = await http.post(`${API_URL}/membership/auto-renewal`, form)

        return response.data
    }

    static async postSwitchAddonAutoRenewal(addonSubscriptionId: string, form: AutoRenewalForm): Promise<any> {
        const response = await http.post(`${API_URL}/membership/auto-renewal/${addonSubscriptionId}`, form)

        return response.data
    }

    static async getMembershipUnsubscribeDetails(): Promise<GetMembershipUnsubscribeDetailsResponse> {
        const response = await http.get(`${API_URL}/membership-unsubscribe-details`)
        return response.data
    }

    static async getGoCardLessSuccess(
        redirectFlowId: string,
        leadId: string | null,
        action: string | null
    ): Promise<PaymentMethodResponse> {
        const response = await http.get(`${API_URL}/gocardless-confirm`, {
            params: {
                redirectFlowId,
                leadId,
                action,
            },
        })
        return response.data
    }

    static async leaveClub(clubId: number | string, comment?: string): Promise<any> {
        const response = await http.post(`${API_URL}/leave-club/${clubId}`, {
            comment,
        })
        return response.data
    }

    static async askRelationship(memberId: string | number, comment?: string) {
        const response = await http.post(`${API_URL}/relationships/${memberId}/asks`, {
            comment,
        })
        return response.data
    }

    static async requestNetworkingAccessToken(): Promise<{ token: string }> {
        const response = await http.post(`${API_URL}/networking/token`)
        return response.data
    }

    static async getRoom(roomId: string): Promise<Room> {
        const { data } = await http.get(`${API_URL}/room/${roomId}`)
        return data
    }

    static async generateRoomSessionToken(roomId: string): Promise<string> {
        const { data } = await http.post(`${API_URL}/room/${roomId}/token`)
        return data?.token
    }

    static async loginCheck({
        username,
        password,
        context,
    }: Login & { context?: string }): Promise<{ redirect: string; refresh_token: string; token: string }> {
        const { data } = await http.post(`${API_URL}/login_check`, {
            _username: username,
            _password: password,
            context,
        })

        return data
    }

    static async refreshToken(currentToken: string, refreshToken: string): Promise<{ token: string }> {
        const response = await http.post(
            `${API_URL}/token`,
            {
                access_token: currentToken,
                refresh_token: refreshToken,
            },
            {
                headers: {
                    Authorization: '',
                },
            }
        )

        return response.data
    }

    static async forceLogin(token?: string): Promise<void> {
        const config: AxiosRequestConfig = {}
        if (token) {
            config.headers = {
                Authorization: `Bearer ${token}`,
            }
        }

        await http.post(`${API_URL}/v3/auth/force-login`, {}, config)
        await http.post(`${PRIVATE_URL}/auth/force-login`, {}, config)
    }

    static async forceLogout(): Promise<void> {
        await http.post(`${PRIVATE_URL}/auth/force-logout`)
    }

    static async register(
        form: Register & { suw: string; context: string }
    ): Promise<ResponseV3<RegisterData, RegisterTokenInfo>> {
        const { data } = await http.post(`${API_URL}/v3/register`, form)

        return data
    }

    static async oauthGoogleLogin(form: {
        context: string
        googleIdToken: string
    }): Promise<ResponseV3<RegisterData, RegisterTokenInfo>> {
        const { data } = await http.post(`${API_URL}/v3/signin/google`, form)

        return data
    }

    static async oauthLinkedinLogin(form: {
        context: string
        linkedinToken: string
        linkedinAuthCode: string
    }): Promise<ResponseV3<RegisterData, RegisterTokenInfo>> {
        const { data } = await http.post(`${API_URL}/v3/signin/linkedin`, form)

        return data
    }

    static async oauthFacebookLogin(form: {
        context: string
        facebookToken: string
    }): Promise<ResponseV3<RegisterData, RegisterTokenInfo>> {
        const { data } = await http.post(`${API_URL}/v3/signin/facebook`, form)

        return data
    }

    static async sendPasswordForgot(username: string, context?: string): Promise<void> {
        await http.post(`${API_URL}/password/forgot`, { username, context })
    }

    static async resetPassword(plainPassword: string, token: string, context?: string): Promise<{ redirect: string }> {
        const { data } = await http.post(`${API_URL}/password/forgot/${token}`, { plainPassword, context })

        return data
    }

    static async joinRoomAsLead(roomId: string, formData: any): Promise<string> {
        const { data } = await http.post(`${API_URL}/room/${roomId}/join-as-lead`, formData)

        return data?.token
    }

    static async getEventLiveJoin(eventId: string | number) {
        const response = await http.get(`${API_URL}/event-streaming/${eventId}/join`)
        return response.data
    }

    /** SEARCH ALL **/

    static async searchAll(type: SearchType, searchText: string, page: number, size: number): Promise<any> {
        const response = await http.post(
            `${API_URL}/v2/search/header/search-all/${type}`,
            { searchText: searchText },
            { params: { page, size } }
        )
        return response.data
    }

    static async getSearchHistory(): Promise<Array<SearchHistory>> {
        const response = await http.get(`${API_URL}/v2/search/history`)

        return response.data
    }

    static async putSearchHistory(searchHistory: SearchHistory): Promise<boolean> {
        const response = await http.put(`${API_URL}/v2/search/history`, searchHistory)

        return response.status === 200
    }

    static async deleteSearchHistory(searchHistory: SearchHistory): Promise<boolean> {
        const response = await http.delete(`${API_URL}/v2/search/history/${searchHistory.id}`)

        return response.status === 200
    }

    static async getBenefits(params: BenefitParams): Promise<ResponseV3<Benefit[], Meta>> {
        const response = await http.get(`${API_URL}/v3/benefits`, {
            params,
        })

        return response.data
    }

    static async getBenefitCategories(): Promise<
        ResponseV3<{ categories: BenefitCategory[]; companies: BenefitCompany[]; cities: BenefitCity[] }, Meta>
    > {
        const response = await http.get(`${API_URL}/v3/benefits/criteria`)

        return response.data
    }

    static async likeBenefit(benefitId: number): Promise<ResponseV3<{ id: number }, Meta>> {
        const response = await http.post(`${API_URL}/v3/benefits/${benefitId}/like`)

        return response.data
    }

    static async unlikeBenefit(benefitId: number): Promise<ResponseV3<{ id: number }, Meta>> {
        const response = await http.delete(`${API_URL}/v3/benefits/${benefitId}/like`)

        return response.data
    }

    static async getLikedBenefits(page = 1): Promise<ResponseV3<Benefit[], Meta>> {
        const response = await http.get(`${API_URL}/v3/benefits/likes`, { params: { page } })

        return response.data
    }

    static async getBenefit(benefitSlug: string): Promise<ResponseV3<FullBenefit, undefined>> {
        const response = await http.get(`${API_URL}/v3/benefits/${benefitSlug}`)

        return response.data
    }

    static async getBenefitSuggestion(benefitId: number): Promise<ResponseV3<Benefit[], undefined>> {
        const response = await http.get(`${API_URL}/v3/benefits/${benefitId}/suggest`)

        return response.data
    }

    static async getWorkspaces(): Promise<ResponseV3<Workspace[], Meta>> {
        const response = await http.get(`${API_URL}/v3/workspaces`)

        return response.data
    }

    static async bookWorkspace(
        workspaceId: number,
        date: string,
        partDay: 'morning' | 'day' | 'afternoon',
        comment: string
    ): Promise<void> {
        await http.post(`${API_URL}/v3/workspaces/${workspaceId}/book`, {
            date,
            comment,
            partDay,
        })
    }

    static async getHeaderConfig(): Promise<ResponseV3<Header, undefined>> {
        const response = await http.get(`${API_URL}/v3/config/header`)

        return response.data
    }

    static async createUnrespondedFeedback(
        relationshipId: number,
        reason?: string,
        comment?: string,
        remindTimeDays?: number | null
    ): Promise<void> {
        await http.post(`${API_URL}/v3/relationship-establishments/${relationshipId}/unresponded-feedback`, {
            reason,
            comment,
            remindTimeDays,
        })
    }

    static async updateRelationship(relationshipId: number, haveTalkedBeforeDate: string): Promise<void> {
        await http.put(`${API_URL}/v3/relationship-establishments/${relationshipId}`, {
            haveTalkedBeforeDate,
        })
    }

    static async getHub(slug: string): Promise<ResponseV3<Hub, undefined>> {
        const response = await http.get(`${API_URL}/v3/hubs/${slug}`)
        return response.data
    }

    static async getHubEvents(
        slug: string,
        params: { page: number; type: 'upcoming' | 'past' }
    ): Promise<ResponseV3<HubEvent[], Meta>> {
        const response = await http.get(`${API_URL}/v3/hubs/${slug}/events`, {
            params,
        })

        return response.data
    }

    static async getHubMembers(slug: string, params: any = {}): Promise<ResponseV3<HubMember[], HubMemberMeta>> {
        const response = await http.post(`${API_URL}/v3/hubs/${slug}/members`, params)

        return response.data
    }

    static async getHubMemberAutocomplete(slug: string, name: string): Promise<ResponseV3<HubMember[], HubMemberMeta>> {
        const response = await http.get(`${API_URL}/v3/hubs/${slug}/members/autocomplete`, {
            params: {
                name,
            },
        })

        return response.data
    }

    static async getClub(slug: string): Promise<ResponseV3<ClubSingle, undefined>> {
        const response = await http.get(`${API_URL}/v3/clubs/${slug}`)
        return response.data
    }

    static async getClubEvents(
        slug: string,
        params: {
            page: number
            type: 'upcoming' | 'past'
        }
    ): Promise<ResponseV3<ClubEvent[], Meta>> {
        const response = await http.get(`${API_URL}/v3/clubs/${slug}/events`, {
            params,
        })

        return response.data
    }

    static async getClubMemberList(slug: string, page = 1): Promise<ResponseV3<ClubMember[], ClubMemberMeta>> {
        const response = await http.get(`${API_URL}/v3/clubs/${slug}/members`, {
            params: {
                page,
            },
        })

        return response.data
    }

    static async getClubMembers(slug: string, params: any = {}): Promise<ResponseV3<ClubMember[], ClubMemberMeta>> {
        const response = await http.post(`${API_URL}/v3/clubs/${slug}/members`, params)

        return response.data
    }

    static async getClubBoard(slug: string): Promise<ResponseV3<ClubBoard[], undefined>> {
        const response = await http.get(`${API_URL}/v3/clubs/${slug}/boards`)

        return response.data
    }

    static async getClubNews(slug: string, params: any = {}): Promise<ResponseV3<ClubNews[], Meta>> {
        const response = await http.get(`${API_URL}/v3/clubs/${slug}/news`, {
            params,
        })

        return response.data
    }

    static async getClubStructuredProjects(): Promise<ResponseV3<ClubProject[], Meta>> {
        const response = await http.get(`${API_URL}/v3/projects/structured`)

        return response.data
    }

    static async getClubMarketplaceProjects(): Promise<ResponseV3<ClubProject[], Meta>> {
        const response = await http.get(`${API_URL}/v3/projects/marketplace`)

        return response.data
    }

    static async getClubMemberAutocomplete(
        slug: string,
        name: string
    ): Promise<ResponseV3<ClubMember[], ClubMemberMeta>> {
        const response = await http.get(`${API_URL}/v3/clubs/${slug}/members/autocomplete`, {
            params: {
                name,
            },
        })

        return response.data
    }

    static async joinClub(slug: string): Promise<void> {
        await http.post(`${API_URL}/v3/clubs/${slug}/join`)
    }

    static async askToJoinClub(slug: string, comment: string): Promise<void> {
        await http.post(`${API_URL}/v3/clubs/${slug}/ask-to-join`, { comment })
    }

    static async getProject(slug: string): Promise<ResponseV3<Project, undefined>> {
        const response = await http.get(`${API_URL}/v3/projects/${slug}`)

        return response.data
    }

    static async addProjectInterest(slug: string, comment: string): Promise<void> {
        await http.post(`${API_URL}/v3/projects/${slug}/interested`, { comment })
    }

    static async addProjectNotInterest(slug: string): Promise<void> {
        await http.post(`${API_URL}/v3/projects/${slug}/not-interested`)
    }

    static async addProjectSponsor(slug: string): Promise<void> {
        await http.post(`${API_URL}/v3/projects/${slug}/sponsor`)
    }

    static async addProjectHelp(slug: string, comment: string): Promise<void> {
        await http.post(`${API_URL}/v3/projects/${slug}/help`, { comment })
    }

    static async payAddon(
        id: string,
        params: { paymentMethod?: string; paymentMethodType: string; paymentMetas?: string }
    ): Promise<void> {
        await http.post(`${API_URL}/v3/addons/${id}/pay`, params)
    }

    static async payClubDonation(
        id: number,
        params: { paymentMethod?: string; paymentMethodType: string; paymentMetas?: string; product: string }
    ): Promise<void> {
        await http.post(`${API_URL}/v3/clubs/${id}/pay`, params)
    }

    static async getDonationProducts(): Promise<ResponseV3<ClubDonation[], Meta>> {
        const response = await http.get(`${API_URL}/v3/products/donation`)

        return response.data
    }

    static async getPartners(params?: GetPartnersForm): Promise<ResponseV3<PartnerItem[], Meta>> {
        const response = await http.get(`${API_URL}/v3/partners`, { params })

        return response.data
    }

    static async getPartnerCategories(): Promise<ResponseV3<PartnerDomains, undefined>> {
        const response = await http.get(`${API_URL}/v3/partners/criteria`)

        return response.data
    }

    static async getPartnerLast(): Promise<ResponseV3<PartnerDomains, undefined>> {
        const response = await http.get(`${API_URL}/v3/partners/last`)

        return response.data
    }

    static async getPartner(slug: string): Promise<ResponseV3<PartnerSingle, undefined>> {
        const response = await http.get(`${API_URL}/v3/partners/${slug}`)

        return response.data
    }

    static async getPartnerMembers(slug: string): Promise<ResponseV3<PartnerMember[], Meta>> {
        const response = await http.get(`${API_URL}/v3/partners/${slug}/members`)

        return response.data
    }

    static async getPartnerEvents(slug: string): Promise<ResponseV3<PartnerEvent[], Meta>> {
        const response = await http.get(`${API_URL}/v3/partners/${slug}/events`)

        return response.data
    }

    static async contactPartner(
        partnerId: number,
        data: {
            subject: string
            message: string
        }
    ): Promise<void> {
        const response = await http.post(`${API_URL}/v3/partners/${partnerId}/contact`, data)

        return response.data
    }

    static async applyPartner(message: string): Promise<void> {
        const response = await http.post(`${API_URL}/v3/partners/ask-to-join`, { message })

        return response.data
    }

    static async createTripPlanner(data: TripAddForm): Promise<ResponseV3<{ id: string | number }, {}>> {
        const response = await http.post(`${API_URL}/v3/trips`, data)

        return response.data
    }

    static async getTrips(type: 'upcoming' | 'past' = 'upcoming', page = 1): Promise<ResponseV3<Trip[], Meta>> {
        const response = await http.get(`${API_URL}/v3/trips`, { params: { type, page } })

        return response.data
    }

    static async getTrip(tripId: string): Promise<ResponseV3<TripSingle, undefined>> {
        const response = await http.get(`${API_URL}/v3/trips/${tripId}`)

        return response.data
    }

    static async getEventsAroundTrip(
        lat: number,
        lng: number,
        from: string,
        to: string
    ): Promise<ResponseV3<[] | TripEventsAround[], Meta>> {
        const response = await http.get(`${API_URL}/v3/external-events/nearest`, { params: { lat, lng, from, to } })

        return response.data
    }

    static async deleteTrip(tripId: string): Promise<void> {
        await http.delete(`${API_URL}/v3/trips/${tripId}`)
    }

    static async getLinkedInAccessToken(form: { code: string; redirectUri: string }) {
        const response = await http.get(`${API_URL}/v3/auth/linkedin/access-token`, { params: form })

        return response.data.data
    }

    static async applyFlow(planId?: string, country?: string): Promise<ResponseV3<Flow, {}>> {
        const response = await http.post(`${API_URL}/v3/apply/flow`, { country, planId })

        return response.data
    }

    static async applyProfile(profile: CommunityApplyProfileForm): Promise<ResponseV3<Flow, {}>> {
        const response = await http.post(`${API_URL}/v3/apply/profile`, profile)

        return response.data
    }

    static async applyMembership(clubIds: number[]) {
        const response = await http.post(`${API_URL}/v3/apply/membership`, { clubIds })

        return response.data
    }

    static async applyCoupon(code: string) {
        const response = await http.post(`${API_URL}/v3/apply/coupon`, { code })

        return response.data
    }

    static async applyAvailableBundle(planId?: string): Promise<ResponseV3<Bundle, {}>> {
        const response = await http.post(`${API_URL}/v3/apply/available-bundles`, { planId })

        return response.data
    }

    static async applyPayment(paymentInfo: CommunityApplyPaymentForm) {
        const response = await http.post(`${API_URL}/v3/apply/payment`, paymentInfo)

        return response.data
    }

    static async applyValidation(validationInfo: CommunityApplyValidationForm) {
        const response = await http.post(`${API_URL}/v3/apply/validation`, validationInfo)

        return response.data
    }

    static async getStakes(): Promise<ResponseV3<Stake[]>> {
        const response = await http.get(`${API_URL}/v3/config/stakes`)

        return response.data
    }

    static async getPendingFeedbackIdList(): Promise<{ id: number }[]> {
        const response = await http.get(`${API_URL}/feedback`)

        return response.data
    }

    static async getFeedback(feedbackId: string | number, feedbackStep?: string): Promise<Feedback> {
        const params = feedbackStep && { step: feedbackStep }
        const response = await http.get(`${API_URL}/feedback/${feedbackId}`, { params })

        return response.data
    }

    static async getAlgoSuggestionsMembers(memberId: string): Promise<ResponseV3<AgreementSuggestionsMembers[]>> {
        const response = await http.get(`${API_URL}/v3/members/${memberId}/suggestions/members`)

        return response.data
    }

    static async getAlgoSuggestionsPartners(memberId: string): Promise<ResponseV3<AgreementSuggestionsPartners[]>> {
        const response = await http.get(`${API_URL}/v3/members/${memberId}/suggestions/partners`)

        return response.data
    }

    static async getAlgoSuggestionsPeers(memberId: string): Promise<ResponseV3<AgreementSuggestionsPeers[]>> {
        const response = await http.get(`${API_URL}/v3/members/${memberId}/suggestions/peers`)

        return response.data
    }

    static async getAlgoSuggestionsMutualInterest(
        memberId: string
    ): Promise<ResponseV3<AgreementSuggestionsMutualInterest[]>> {
        const response = await http.get(`${API_URL}/v3/members/${memberId}/suggestions/mutual-interest`)

        return response.data
    }

    static async postAlgoSuggestionPing(firstMemberId: string, secondMemberId: string): Promise<void> {
        const response = await http.post(`${API_URL}/v3/members/suggestion/ping/${firstMemberId}/${secondMemberId}`)

        return response.data
    }

    static async sendAgreementNeedToSlack(data: NeedToSlack): Promise<void> {
        await http.post(`${API_URL}/v3/agreement/slack`, data)
    }

    static async createAgreement(data: any): Promise<ResponseV3<AgreementType>> {
        const response = await http.post(`${API_URL}/v3/agreement`, data)

        return response.data
    }

    static async getAgreement(memberId: string | number): Promise<ResponseV3<AgreementType>> {
        const response = await http.get(`${API_URL}/v3/agreement/${memberId}`)

        return response.data
    }

    static async getAgreementList(memberId: string): Promise<ResponseV3<AgreementType[]>> {
        const response = await http.get(`${API_URL}/v3/agreement/${memberId}/list`)

        return response.data
    }

    static async approveAgreement(memberId: string): Promise<ResponseV3<AgreementType>> {
        const response = await http.post(`${API_URL}/v3/agreement/${memberId}/approve`)

        return response.data
    }

    static async cancelAgreementAmendment(memberId: string): Promise<ResponseV3<AgreementType>> {
        const response = await http.post(`${API_URL}/v3/agreement/${memberId}/cancel-amendment`)

        return response.data
    }

    static async getMemberRelationsByNeeds(memberId: string): Promise<ResponseV3<AgreementNeedsMemberHelping[]>> {
        const response = await http.get(`${API_URL}/v3/agreement/${memberId}/relation_establishment`)

        return response.data
    }

    static async getMemberRelationsByHelp(memberId: string): Promise<ResponseV3<AgreementNeedsMemberHelping[]>> {
        const response = await http.get(`${API_URL}/v3/agreement/${memberId}/relations_help`)

        return response.data
    }

    static async feedbackPingAdmin(feedbackId: string | number): Promise<void> {
        await http.get(`${API_URL}/feedback/${feedbackId}/ping/admin`)
    }

    static async postFeedbackAnswers(feedbackId: string | number, answers: any): Promise<any> {
        const response = await http.post(`${API_URL}/feedback/${feedbackId}`, { ...answers })

        return response.data
    }

    static async getFeedbackBack(feedbackId: string | number, form_step: any): Promise<any> {
        const response = await http.get(`${API_URL}/feedback/${feedbackId}/back`, {
            params: {
                form_step,
            },
        })

        return response.data
    }

    static async createSponsorship(sponsorships: InvitationItem[] | Invitation[], message: string): Promise<void> {
        await http.post(`${API_URL}/v2/sponsorships`, {
            validate: message === 'Lorem Ipsum',
            sponsorships,
            message,
        })
    }

    static async getYearlyConvertedSponsorship(): Promise<Sponsorship[]> {
        const response = await http.get(`${API_URL}/v2/sponsorships/yearly-converted`)

        return response.data
    }

    static async getNotConvertedSponsorships(): Promise<Sponsorship[]> {
        const response = await http.get(`${API_URL}/v2/sponsorships/not-converted`)

        return response.data
    }

    static async getEmailInvitation() {
        const response = await http.get(`${API_URL}/v2/sponsorship/email/invitation`)

        return response.data
    }

    static async getParentsActivity(): Promise<ResponseV3<[]>> {
        const response = await http.get(`${API_URL}/v3/activity/parents`)

        return response.data
    }

    static async sendInvestorClubOnboardingInfo(data: InvestmentInquiryFormDTO): Promise<ResponseV3<{}>> {
        const response = await http.patch(`${API_URL}/v3/members/me`, data)

        return response.data
    }

    static async getTeam(): Promise<ResponseV3<GetTeamResponse>> {
        const response = await http.get(`${API_URL}/v3/ff-team`)

        return response.data
    }

    static async getHomePageOverview(): Promise<ResponseV3<GetHomeOverviewResponse>> {
        const response = await http.get(`${API_URL}/v3/club-homepage/overview`)

        return response.data
    }

    static async getHomeSuggestedEvents(): Promise<ResponseV3<GetSuggestedEventsResponse>> {
        const response = await http.get(`${API_URL}/v3/club-homepage/suggested-events`)

        return response.data
    }

    static async getHomeBanner(): Promise<ResponseV3<GetHomeBannerResponse>> {
        const response = await http.get(`${API_URL}/v3/club-homepage/banners`)

        return response.data
    }

    static async getDiscoverClubs(): Promise<ResponseV3<GetDiscoverClubsResponse>> {
        const response = await http.get(`${API_URL}/v3/clubs`)

        return response.data
    }

    static async getMemberInTrip(): Promise<ResponseV3<GetMemberInTripResponse>> {
        const response = await http.get(`${API_URL}/v3/club-homepage/in-trip`)

        return response.data
    }

    static async getNextEventsInHub(): Promise<ResponseV3<GetNextEventsInHubResponse>> {
        const response = await http.get(`${API_URL}/v3/club-homepage/hub-events`)

        return response.data
    }

    static async getLastBatch(): Promise<ResponseV3<GetLastBatchResponse, { batch: string }>> {
        const response = await http.get(`${API_URL}/v3/club-homepage/last-batch`)

        return response.data
    }

    static async sendTracks(event: string, data?: unknown): Promise<unknown> {
        if (!process.env?.VUE_APP_ENV || process.env?.VUE_APP_ENV === 'dev') {
            // Do not remove console log - Used to keep track of skipped wayd events
            console.log('[DEBUG] Skipped wayd event:', event, data)
            return
        }

        const response = await http.post(`${API_URL}/v3/wayd`, {
            event,
            data: JSON.stringify(data),
        })

        return response.data
    }
}

http.interceptors.response.use(
    async response => {
        await forceRefreshUser(response)

        // maintenance mode
        if (response?.status === 210) {
            await store.dispatch('app/setMaintenance', true)
        }

        return response
    },
    async error => {
        if (error.config?.params?.__retry) {
            throw error
        }

        if (error.config) {
            if (typeof error.response === 'undefined' && error.code !== 'ECONNABORTED') {
                if (!error.config.params) {
                    error.config.params = {}
                }

                error.config.params.__retry = true
                return setTimeout(() => {
                    if (error.config) {
                        axios.request(error.config)
                    }
                }, 1_000)
            }
        }

        await forceRefreshUser(error.response)
        if (error.isAxiosError) {
            const axiosError: AxiosError = error

            if (axiosError.response?.status === 412) {
                if (router.currentRoute.name !== 'membership-overview') {
                    await router.replace({ name: 'membership-overview' })
                }
            } else if (
                axiosError.response?.status === 401 &&
                axiosError.response.config.url !== `${API_URL}/login_check`
            ) {
                const token = window.currentJwt || SharedStorage.get(jwtTokenName) || null
                const refreshToken = window.currentJwtRefresh || SharedStorage.get(refreshJwtTokenName) || null

                if (token && refreshToken) {
                    try {
                        const { token: newToken } = await API.refreshToken(token, refreshToken)
                        await store.dispatch('user/setToken', newToken)
                        console.warn('[interceptor] Force login ...')
                        await API.forceLogin(newToken)
                        window.location.reload()
                    } catch (e) {
                        console.error(e)
                        await store.dispatch('user/logout')
                    }
                } else {
                    await store.dispatch('user/logout', '/login')
                }
            }
        }

        return Promise.reject(error)
    }
)

export default API
